import { fetchVariantLabelInfo } from './api/catalog.ts'
import { addToLabelQueue } from './label-queue.ts'
import type { LabelDemand } from './received-labels.ts'

/**
 * Pôr as etiquetas de uma compra na fila — um caminho só (F195).
 *
 * Quem acabou de receber e quem reabre a compra recebida ontem chegam aqui: a
 * diferença entre os dois é só de onde vem a quantidade (`labelsFromReceipt`
 * ou `labelsFromPurchase`).
 *
 * ## Em blocos de 100
 *
 * `label-info?ids=` aceita até 100 ids. A primeira versão mandava todos de uma
 * vez: uma nota com 101 produtos diferentes era um erro na hora de etiquetar.
 *
 * ## Com fornecedor e localização
 *
 * Os campos do modelo da F141 vêm junto — sem eles a prévia saía sem a
 * referência até a tela de Etiquetas reler os preços.
 */

const CHUNK = 100

export type PurchaseLabelsQueued = {
  /** Variantes que o catálogo não devolveu — produto excluído desde a compra. */
  readonly missing: number
  /** Variantes que não couberam na fila. */
  readonly dropped: number
}

export const queuePurchaseLabels = async (
  tenantId: string,
  demand: readonly LabelDemand[]
): Promise<PurchaseLabelsQueued> => {
  const ids = demand.map((item) => item.variantId)
  const chunks: string[][] = []
  for (let at = 0; at < ids.length; at += CHUNK) {
    chunks.push(ids.slice(at, at + CHUNK))
  }
  const found = (
    await Promise.all(chunks.map((chunk) => fetchVariantLabelInfo(chunk)))
  ).flatMap((list) => list.items)
  const byId = new Map(found.map((item) => [item.variantId, item]))

  const items = demand.flatMap((wanted) => {
    const item = byId.get(wanted.variantId)
    return item === undefined
      ? []
      : [
          {
            variantId: item.variantId,
            productId: item.productId,
            name: item.name,
            sku: item.sku,
            gtin: item.gtin,
            salePrice: item.salePrice,
            brand: item.brand,
            unit: item.unit,
            supplierReference: item.supplierReference ?? null,
            location: item.location ?? null,
            quantity: wanted.labels,
          },
        ]
  })

  const { dropped } = addToLabelQueue(tenantId, items)
  return { missing: demand.length - items.length, dropped }
}
